import CruciblePiece from './cruciblePiece';
import * as THREE from 'three';
import * as config from '../config.json';
import { RunesConfig } from '../config';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';
import CrucibleData from 'helpers/nft/crucibleData';
import CrucibleLoader from '../crucibleLoader';
import { Achievement, UVRange, achievements } from '../achievements';

export class Runes extends CruciblePiece {
  protected _config: RunesConfig;
  protected _runeButtons: THREE.Object3D[];
  protected _achievementRunes: THREE.Object3D[];

  protected _runeOnClickYOffset: number;
  protected _runeOriginalYPos: number;

  constructor() {
    super();
    this._config = config.runes;
    this._runeButtons = [];
    this._achievementRunes = [];

    this._runeOnClickYOffset = -15;
    this._runeOriginalYPos = 0;
  }

  setup(gltf: GLTF) {
    this.group = gltf.scene;
    this.group.scale.set(2, 2, 2); // This should be removed with final models'

    this._runeButtons = [];
    this._achievementRunes = [];

    this.group.traverse((node: any) => {
      if (node instanceof THREE.Mesh) {
        node.castShadow = true;
        node.receiveShadow = true;
        if (node.material.map) node.material.map.anisotropy = 16;
      }

      if (node.name === this._config.runesNodeName) {
        this._runeButtons = node.children;
        // Set the initial Y position of the first rune mesh node to be used in playOnClick
        this._runeOriginalYPos = node.children[0].position.y;
      }
    });
  }

  animate(timeElapsed: number) {
    this.group.rotation.y += 0.04 * timeElapsed;

    // Not animating the achievements runes anymore

    // if (this._achievementRunes.length > 0) {
    //   this._achievementRunes.forEach((achievementRune) => {
    //     achievementRune.rotation.y += 0.6 * timeElapsed;
    //   });
    // }
  }

  playOnClick(raycaster: THREE.Raycaster) {
    const intersects = raycaster.intersectObjects(this._runeButtons, true);

    if (intersects.length > 0) {
      if (!intersects[0].object.parent) return;

      let clickedRune = intersects[0].object.parent;

      // In case the user clicks in the achievement icon, we have to get the parent's parent
      if (!this._runeButtons.includes(intersects[0].object.parent)) {
        // @ts-ignore
        clickedRune = intersects[0].object.parent.parent;
      }

      // TODO: Add a tween animation here later instead of just setting the position
      if (clickedRune.position.y === this._runeOriginalYPos) {
        clickedRune.position.y += this._runeOnClickYOffset;
      } else {
        clickedRune.position.y = this._runeOriginalYPos;
      }
    }
  }

  update(data: CrucibleData, loader: CrucibleLoader, color: THREE.Color) {
    // Set Crucible Achievements
    const userAchievements = this._checkAchievements(data);

    if (userAchievements.length > 0) {
      // Load achievement Runes Atlas Texture
      const runeTexture = loader.textureLoader.load(this._config.runesAtlasUrl);

      // Create one material to be used in all achievements
      const runePlaneMaterial = new THREE.MeshStandardMaterial({
        map: runeTexture,
        transparent: true,
        depthWrite: true,
        color: color,
        emissive: color,
        emissiveIntensity: 2,
        side: THREE.DoubleSide
      });

      userAchievements.forEach((achievement) => {
        this._drawAchievementRuneIcon(achievement, runePlaneMaterial);
      });
    }
  }

  _drawAchievementRuneIcon(
    achievement: Achievement,
    material: THREE.MeshStandardMaterial
  ) {
    // Create Rune icon plane
    const runePlaneGeometry = new THREE.PlaneGeometry(80, 80);

    // Modify plane UV based on position in Texture Atlas
    this._modifyPlaneUV(runePlaneGeometry, achievement.range);

    const runePlaneMesh = new THREE.Mesh(runePlaneGeometry, material);

    // Look for the right rune node
    this.group.children[0].traverse((node) => {
      if (node.name === achievement.runeNodeName) {
        // Add plane as child the right node
        node.children[0].add(runePlaneMesh);
        runePlaneMesh.position.set(0, 30, 0);
        runePlaneMesh.rotation.set(0, 0, 0);
        // runePlaneMesh.rotateZ(Math.PI / 2.2);
        // runePlaneMesh.rotateY(Math.PI / 2);
        runePlaneMesh.lookAt(0, 70, 0);

        // Press down the correspondent rune
        node.position.y += this._runeOnClickYOffset;
      }
    });

    // Save plane object reference to animate
    this._achievementRunes.push(runePlaneMesh);
  }

  _modifyPlaneUV(geometry: THREE.BufferGeometry, range: UVRange) {
    const uvAttr = geometry.attributes.uv;

    uvAttr.setXY(0, range.startU, range.endV);
    uvAttr.setXY(1, range.endU, range.endV);
    uvAttr.setXY(2, range.startU, range.startV);
    uvAttr.setXY(3, range.endU, range.startV);
  }

  _checkAchievements(data: CrucibleData): Achievement[] {
    let userAchievements: Achievement[] = [];

    // Aludel v1 participation
    const historicalAludelv1 = data.historicalRewardPrograms.filter(
      (rewardProgram) => {
        return (
          rewardProgram.rewardProgramAddress ===
          '0xf0D415189949d913264A454F57f4279ad66cB24d'
        );
      }
    );

    const currentAludelv1 = data.currentRewardPrograms.filter(
      (rewardProgram) => {
        return (
          rewardProgram.rewardProgramAddress ===
          '0xf0D415189949d913264A454F57f4279ad66cB24d'
        );
      }
    );

    if (historicalAludelv1 || currentAludelv1) {
      userAchievements.push(achievements['aludelv1']);
      // console.log('Achievement Unlocked: Aludel v1');
    }

    // Reached 100% Multiplier in Reward Programs
    const fullMultiplierPrograms = data.historicalRewardPrograms.filter(
      (rewardProgram) => {
        return rewardProgram.multiplier === 1;
      }
    );

    // Multiplier 1: 100% multiplier in at least 1 program
    if (fullMultiplierPrograms.length >= 1) {
      userAchievements.push(achievements['fullMultiplier1']);
      // console.log('Achievement Unlocked: 100% Multiplier 1');
    }

    // Multiplier 2: 100% multiplier in at least 3 programs
    if (fullMultiplierPrograms.length >= 3) {
      userAchievements.push(achievements['fullMultiplier2']);
      // console.log('Achievement Unlocked: 100% Multiplier 2');
    }

    // Participated in 3 or more reward programs
    if (data.historicalRewardPrograms.length >= 3) {
      userAchievements.push(achievements['rewardPrograms1']);
      // console.log('Achievement Unlocked: Reward Programs 1');
    }

    return userAchievements;
  }
}
